import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 30C Phase 2 — DOCX -> Interactive Blocks import
 * preview/publish API client. Mirrors fileQueries.js's `request()`
 * error contract exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.message || body.detail?.error || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const diqk = {
  detail: (wrkId, importId) => ["docx-import", wrkId, importId],
};

export function useCreateImportPreview(workspaceId) {
  return useMutation({
    mutationFn: (fasId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/import-preview`, { method: "POST" }),
  });
}

export function useImportPreview(workspaceId, importId, options = {}) {
  return useQuery({
    queryKey: diqk.detail(workspaceId, importId),
    enabled: !!workspaceId && !!importId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/imports/${importId}`),
    ...options,
  });
}

export function useUpdateImportPreview(workspaceId, importId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/imports/${importId}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: (data) => qc.setQueryData(diqk.detail(workspaceId, importId), data),
  });
}

export function usePublishImport(workspaceId, importId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/imports/${importId}/publish`, { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-documents", workspaceId] }),
  });
}

export function useCancelImport(workspaceId) {
  return useMutation({
    mutationFn: (importId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/imports/${importId}/cancel`, { method: "POST" }),
  });
}
